import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import { API_BASE_URL } from '../../../constants/constants';
import { jwtDecode } from 'jwt-decode';
import { LEVEL_NAMES } from '../../../constants/userLevels';
import { usePermissions } from '../../../context/PermissionContext';
import BackButton from '../../../components/BackButton';

const CreateAdmin = () => {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Status Change Confirmation Modal State
  const [confirmStatusModalOpen, setConfirmStatusModalOpen] = useState(false);
  const [targetUserForStatus, setTargetUserForStatus] = useState(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [address, setAddress] = useState('');
  const [level, setLevel] = useState('');
  const [pinCode, setPinCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('active');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Edit Admin Modal State
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    address: '',
    pinCode: '',
    level: 2,
  });
  const [editValidationErrors, setEditValidationErrors] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');
  const [userLevel, setUserLevel] = useState(null);
  const { can } = usePermissions();
  const [userId, setUserId] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  // Dialog state for showing lists of Districts / Villages / Pincodes
  const [listDialogOpen, setListDialogOpen] = useState(false);
  const [listDialogTitle, setListDialogTitle] = useState('');
  const [listDialogItems, setListDialogItems] = useState([]);

  const levelNames = LEVEL_NAMES;

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      const decodedToken = jwtDecode(token);
      setUserLevel(decodedToken.level);
      setUserId(decodedToken.userId);
      setLevel(decodedToken.level + 1);
    }
  }, []);

  useEffect(() => {
    if (userId) {
      fetchUsers();
    }
  }, [userId, filterStatus, filterLevel, searchTerm]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/users/all/${userId}`, {
        params: {
          status: filterStatus,
          level: filterLevel,
          search: searchTerm,
        },
      });
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleFilterStatusChange = (event) => {
    setFilterStatus(event.target.value);
  };

  const handleFilterLevelChange = (event) => {
    setFilterLevel(event.target.value);
  };

  const handleExportCSV = async () => {
    if (!can('users.admins.export')) return;
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/users/export-csv/${userId}`, {
        params: {
          status: filterStatus,
          level: filterLevel,
          search: searchTerm,
        },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `admin_users_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting CSV:', error);
      setSnackbarMessage('Failed to export CSV');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleOpenDialog = () => {
    if (!can('users.admins.create')) return;
    setOpen(true);
  };

  const handleCloseDialog = () => {
    if (firstName || lastName || address || level || pinCode || phoneNumber || email) {
      setConfirmDialogOpen(true);
    } else {
      clearDialogData();
    }
  };

  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false);
  };

  const handleConfirmDialogProceed = () => {
    setConfirmDialogOpen(false);
    clearDialogData();
  };

  const clearDialogData = () => {
    setOpen(false);
    setFirstName('');
    setLastName('');
    setAddress('');
    setLevel(userLevel + 1);
    setPinCode('');
    setPhoneNumber('');
    setEmail('');
    setStatus('active');
    setSnackbarMessage('');
    setValidationErrors({});
  };

  const validateForm = () => {
    const errors = {};
    if (!/^\d{10}$/.test(phoneNumber)) {
      errors.phoneNumber = 'Phone number must be 10 digits.';
    }
    if (!/^\d{6}$/.test(pinCode)) {
      errors.pinCode = 'Pin code must be a 6-digit number.';
    }
    if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email)) {
      errors.email = 'Enter a valid email address.';
    }
    return errors;
  };

  const handleAddUser = async () => {
    if (!can('users.admins.create')) return;
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setSnackbarMessage('Please fix the errors in the form.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      const res = await axios.post(
        `${API_BASE_URL}/users/create`,
        {
          firstName,
          lastName,
          address,
          level,
          pinCode,
          phoneNumber,
          email,
          createdBy: userId,
          status: status || 'active',
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const successMsg = res.data.stateHeadId
        ? `User created successfully! State Head ID: ${res.data.stateHeadId}`
        : 'User created successfully';

      setSnackbarMessage(successMsg);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      clearDialogData();
      fetchUsers();
    } catch (error) {
      const errMsg =
        error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'Error creating user';
      setSnackbarMessage(errMsg);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      console.error('Error creating user:', error);
    }
  };

  const handleOpenStatusConfirm = (user) => {
    setTargetUserForStatus(user);
    setConfirmStatusModalOpen(true);
  };

  const handleOpenEditModal = (row) => {
    if (!can('users.admins.edit')) return;
    setUserToEdit(row);
    setEditForm({
      firstName: row.firstName || '',
      lastName: row.lastName || '',
      phoneNumber: row.phoneNumber || '',
      email: row.email || '',
      address: row.address || '',
      pinCode: row.pinCode || '',
      level: row.rawLevel || 2,
    });
    setEditValidationErrors({});
    setEditModalOpen(true);
  };

  const handleSaveEditUser = async () => {
    if (!userToEdit) return;
    if (!can('users.admins.edit')) {
      setSnackbarMessage('You do not have permission to edit users');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    const errors = {};
    if (!/^\d{10}$/.test(editForm.phoneNumber)) {
      errors.phoneNumber = 'Phone number must be 10 digits.';
    }
    if (!/^\d{6}$/.test(editForm.pinCode)) {
      errors.pinCode = 'Pin code must be 6 digits.';
    }
    if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(editForm.email)) {
      errors.email = 'Enter a valid email address.';
    }

    if (Object.keys(errors).length > 0) {
      setEditValidationErrors(errors);
      setSnackbarMessage('Please fix form validation errors.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/users/update/${userToEdit.id}`,
        {
          ...editForm,
          requesterId: userId,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setSnackbarMessage('Admin user updated successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setEditModalOpen(false);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      const errMsg = error.response?.data?.message || 'Error updating user';
      setSnackbarMessage(errMsg);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleConfirmStatusToggle = async () => {
    if (!targetUserForStatus) return;
    if (!can('users.admins.changeStatus')) return;
    try {
      const currentStatus = targetUserForStatus.status || 'active';
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      await axios.patch(`${API_BASE_URL}/users/update-status/${targetUserForStatus.id}`, {
        status: newStatus,
        requesterId: userId,
      });

      setSnackbarMessage(
        `User ${targetUserForStatus.firstName} ${targetUserForStatus.lastName} ${
          newStatus === 'active' ? 'activated' : 'deactivated'
        } successfully`
      );
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchUsers();
    } catch (error) {
      const errMsg =
        error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'Error updating user status';
      setSnackbarMessage(errMsg);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      console.error('Error updating status:', error);
    } finally {
      setConfirmStatusModalOpen(false);
      setTargetUserForStatus(null);
    }
  };

  const handleOpenListDialog = (title, items) => {
    setListDialogTitle(title);
    setListDialogItems(items || []);
    setListDialogOpen(true);
  };

  const rows = users.map((user) => {
    const levelNum = Number(user.level);

    let qpayAgentIdVal = 'not required';
    let qpayCenterIdVal = 'not required';
    let stateStaffIdVal = 'not required';
    let stateHeadIdVal = 'not required';

    if (levelNum === 5) {
      qpayAgentIdVal = user.qpayAgentId || user.agentId || user.customId || user.stateHeadId || 'N/A';
    }
    if (levelNum === 4) {
      qpayCenterIdVal = user.qpayCenterId || user.qpayCentreId || user.centerId || user.customId || user.stateHeadId || 'N/A';
    }
    if (levelNum === 3) {
      stateStaffIdVal = user.stateStaffId || user.staffId || user.customId || user.stateHeadId || 'N/A';
    }
    if (levelNum === 2) {
      stateHeadIdVal = user.stateHeadId || user.customId || 'N/A';
    } else if (user.stateHeadId) {
      stateHeadIdVal = user.stateHeadId;
    }

    return {
      id: user._id,
      qpayAgentId: qpayAgentIdVal,
      qpayCenterId: qpayCenterIdVal,
      stateStaffId: stateStaffIdVal,
      stateHeadId: stateHeadIdVal,
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      address: user.address || '',
      phoneNumber: user.phoneNumber || '',
      email: user.email || '',
      level: levelNames[user.level] || user.level,
      rawLevel: user.level,
      pinCode: user.pinCode || '',
      status: user.status || 'active',
      states: user.states || (user.stateCode ? [user.stateCode] : ['Kerala']),
      districts: user.districts || [],
      villages: user.villages || [],
      pincodes: user.pincodes || [],
    };
  });

  const getColumns = () => {
    const cols = [
      {
        field: 'sl',
        headerName: 'SL',
        width: 60,
        sortable: false,
        renderCell: (params) => {
          const index = rows.findIndex((r) => r.id === params.row.id);
          return (
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#64748b' }}>
              {index >= 0 ? index + 1 : ''}
            </Typography>
          );
        },
      },
    ];

    // Q-Pay Agent ID (Visible for levels 1, 2, 3, 4)
    cols.push({
      field: 'qpayAgentId',
      headerName: 'Q-Pay Agent ID',
      width: 150,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: params.value === 'not required' ? 400 : 700,
            color: params.value === 'not required' ? '#94a3b8' : '#0f172a',
            fontStyle: params.value === 'not required' ? 'italic' : 'normal',
          }}
        >
          {params.value}
        </Typography>
      ),
    });

    // Q-Pay Centre ID (Visible for levels 1, 2, 3)
    if (userLevel <= 3) {
      cols.push({
        field: 'qpayCenterId',
        headerName: 'Q-Pay Centre ID',
        width: 150,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{
              fontWeight: params.value === 'not required' ? 400 : 700,
              color: params.value === 'not required' ? '#94a3b8' : '#0f172a',
              fontStyle: params.value === 'not required' ? 'italic' : 'normal',
            }}
          >
            {params.value}
          </Typography>
        ),
      });
    }

    // State Officer ID (Visible for levels 1, 2)
    if (userLevel <= 2) {
      cols.push({
        field: 'stateStaffId',
        headerName: 'State Officer ID',
        width: 140,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{
              fontWeight: params.value === 'not required' ? 400 : 700,
              color: params.value === 'not required' ? '#94a3b8' : '#0f172a',
              fontStyle: params.value === 'not required' ? 'italic' : 'normal',
            }}
          >
            {params.value}
          </Typography>
        ),
      });
    }

    // State Head ID (Visible ONLY for level 1 SuperAdmin)
    if (userLevel === 1) {
      cols.push({
        field: 'stateHeadId',
        headerName: 'State Head ID',
        width: 140,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{
              fontWeight: params.value === 'not required' ? 400 : 700,
              color: params.value === 'not required' ? '#94a3b8' : '#0f172a',
              fontStyle: params.value === 'not required' ? 'italic' : 'normal',
            }}
          >
            {params.value}
          </Typography>
        ),
      });
    }

    // Common Profile Fields for all levels
    cols.push(
      { field: 'firstName', headerName: 'First Name', width: 130 },
      { field: 'lastName', headerName: 'Last Name', width: 130 },
      { field: 'phoneNumber', headerName: 'Mobile Number', width: 140 },
      { field: 'address', headerName: 'Address', width: 180 },
      { field: 'email', headerName: 'Email', width: 180 },
      {
        field: 'states',
        headerName: 'List of STATE',
        width: 155,
        sortable: false,
        renderCell: (params) => {
          const list = params.row.states || ['Kerala'];
          return (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => handleOpenListDialog(`States for ${params.row.firstName}`, list)}
              sx={{ height: 32, my: 'auto', textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              {list.length > 0 ? `States (${list.length})` : 'None'}
            </Button>
          );
        },
      },
      {
        field: 'districts',
        headerName: 'List of DISTRICT',
        width: 180,
        sortable: false,
        renderCell: (params) => {
          const list = params.row.districts || [];
          return (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => handleOpenListDialog(`Districts for ${params.row.firstName}`, list)}
              sx={{ height: 32, my: 'auto', textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              {list.length > 0 ? `Districts (${list.length})` : 'None'}
            </Button>
          );
        },
      },
      {
        field: 'villages',
        headerName: 'List of CONSTITUENCY',
        width: 200,
        sortable: false,
        renderCell: (params) => {
          const list = params.row.villages || [];
          return (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => handleOpenListDialog(`Constituencies for ${params.row.firstName}`, list)}
              sx={{ height: 32, my: 'auto', textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              {list.length > 0 ? `Constituencies (${list.length})` : 'None'}
            </Button>
          );
        },
      },
      {
        field: 'pincodes',
        headerName: "List of PINCODE's",
        width: 180,
        sortable: false,
        renderCell: (params) => {
          const list = params.row.pincodes || [];
          return (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => handleOpenListDialog(`Pincodes for ${params.row.firstName}`, list)}
              sx={{ height: 32, my: 'auto', textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              {list.length > 0 ? `Pincodes (${list.length})` : 'None'}
            </Button>
          );
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 175,
        minWidth: 175,
        renderCell: (params) => {
          if (can('users.admins.changeStatus')) {
            return (
              <Button
                variant="contained"
                size="small"
                color={params.value === 'active' ? 'secondary' : 'primary'}
                onClick={() => handleOpenStatusConfirm(params.row)}
                sx={{ fontWeight: 700, height: 32, my: 'auto', whiteSpace: 'nowrap', px: 2 }}
              >
                {params.value === 'active' ? 'Deactivate' : 'Activate'}
              </Button>
            );
          }
          return (
            <Chip
              label={params.value === 'active' ? 'Active' : 'Inactive'}
              color={params.value === 'active' ? 'success' : 'default'}
              size="small"
              sx={{ fontWeight: 700, my: 'auto' }}
            />
          );
        },
      }
    );

    // Edit Actions Column
    if (can('users.admins.edit')) {
      cols.push({
        field: 'actions',
        headerName: 'Actions',
        width: 110,
        sortable: false,
        renderCell: (params) => (
          <Tooltip title="Edit Admin">
            <IconButton
              color="primary"
              size="small"
              onClick={() => handleOpenEditModal(params.row)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        ),
      });
    }

    return cols;
  };

  return (
    <Box sx={{ p: 3, pr: 4, mb: 4, width: '100%', boxSizing: 'border-box' }}>
      <BackButton />
      <br />
      <br />
      <Paper elevation={0} sx={{ p: 3.5, pr: 4, borderRadius: 3, border: '1px solid #e2e8f0', background: '#ffffff', boxSizing: 'border-box' }}>
        {/* Page Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, pr: 1 }}>
          <Box>
            <Typography variant="h5" color="primary" sx={{ fontWeight: 800, mb: 0.5 }}>
              Admin Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create, view, and manage administrative profiles across all system levels
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {can('users.admins.export') && (
              <Button
                variant="contained"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={handleExportCSV}
                sx={{ fontWeight: 700 }}
              >
                Export CSV
              </Button>
            )}
            {can('users.admins.create') && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleOpenDialog}
                sx={{ fontWeight: 700 }}
              >
                Create New Admin
              </Button>
            )}
          </Box>
        </Box>

        {/* Filter Section */}
        <Paper elevation={0} sx={{ p: 2.5, px: 3, mb: 3, mt: 2, borderRadius: 2, border: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search Name, Email, Mobile or ID..."
                variant="outlined"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={3.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={filterStatus} onChange={handleFilterStatusChange} label="Status">
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3.5}>
              <FormControl fullWidth size="small">
                <InputLabel>User Type</InputLabel>
                <Select value={filterLevel} onChange={handleFilterLevelChange} label="User Type">
                  <MenuItem value="">All Types</MenuItem>
                  {userLevel <= 2 && <MenuItem value="2">{LEVEL_NAMES[2]}</MenuItem>}
                  {userLevel <= 3 && <MenuItem value="3">{LEVEL_NAMES[3]}</MenuItem>}
                  {userLevel <= 4 && <MenuItem value="4">{LEVEL_NAMES[4]}</MenuItem>}
                  {userLevel <= 5 && <MenuItem value="5">{LEVEL_NAMES[5]}</MenuItem>}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        <Box sx={{ height: 550, width: '100%', overflow: 'auto' }}>
          <DataGrid
            rows={rows}
            columns={getColumns()}
            pageSize={10}
            rowsPerPageOptions={[10, 20, 50]}
            disableSelectionOnClick
            rowHeight={60}
            sx={{
              border: 'none',
              width: '100%',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f8fafc',
                fontWeight: 700,
                color: '#1e293b',
                fontSize: '0.85rem',
                px: 1,
              },
              '& .MuiDataGrid-cell': {
                py: 1.5,
                px: 1.5,
                display: 'flex',
                alignItems: 'center',
                whiteSpace: 'nowrap',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#f8fafc',
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '1px solid #e2e8f0',
                px: 2,
              },
            }}
          />
        </Box>
      </Paper>

        {/* Dialog for displaying list of Districts / Villages / Pincodes */}
        <Dialog open={listDialogOpen} onClose={() => setListDialogOpen(false)} fullWidth maxWidth="xs">
          <DialogTitle sx={{ fontWeight: 700 }}>{listDialogTitle}</DialogTitle>
          <DialogContent dividers>
            {listDialogItems.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {listDialogItems.map((item, idx) => (
                  <Chip key={idx} label={String(item)} color="primary" variant="outlined" />
                ))}
              </Box>
            ) : (
              <Typography color="text.secondary" align="center">
                No items assigned yet.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setListDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Create Admin Dialog */}
        <Dialog open={open} onClose={handleCloseDialog} fullWidth maxWidth="sm">
          <DialogTitle sx={{ fontWeight: 700 }}>Create New Admin</DialogTitle>
          <DialogContent>
            <TextField
              label="First Name"
              variant="outlined"
              fullWidth
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              sx={{ mb: 2, mt: 1 }}
            />
            <TextField
              label="Last Name"
              variant="outlined"
              fullWidth
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Address"
              variant="outlined"
              fullWidth
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              label="User Type"
              variant="outlined"
              fullWidth
              value={levelNames[level] || level}
              disabled
              sx={{ mb: 2 }}
            />
            <TextField
              label="Pin Code"
              variant="outlined"
              fullWidth
              value={pinCode}
              onChange={(e) => setPinCode(e.target.value)}
              sx={{ mb: 2 }}
              error={!!validationErrors.pinCode}
              helperText={validationErrors.pinCode}
            />
            <TextField
              label="Phone Number"
              variant="outlined"
              fullWidth
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              sx={{ mb: 2 }}
              error={!!validationErrors.phoneNumber}
              helperText={validationErrors.phoneNumber}
            />
            <TextField
              label="Email"
              variant="outlined"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2 }}
              error={!!validationErrors.email}
              helperText={validationErrors.email}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select value={status} onChange={(e) => setStatus(e.target.value)} label="Status">
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="secondary">
              Cancel
            </Button>
            <Button onClick={handleAddUser} color="primary" variant="contained">
              Submit
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Admin Modal */}
        <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle sx={{ fontWeight: 700 }}>Edit Admin Details</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="First Name"
                  fullWidth
                  value={editForm.firstName}
                  onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Last Name"
                  fullWidth
                  value={editForm.lastName}
                  onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone Number"
                  fullWidth
                  value={editForm.phoneNumber}
                  onChange={(e) => setEditForm({ ...editForm, phoneNumber: e.target.value })}
                  error={!!editValidationErrors.phoneNumber}
                  helperText={editValidationErrors.phoneNumber}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  fullWidth
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  error={!!editValidationErrors.email}
                  helperText={editValidationErrors.email}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>User Type / Level</InputLabel>
                  <Select
                    value={editForm.level}
                    onChange={(e) => setEditForm({ ...editForm, level: Number(e.target.value) })}
                    label="User Type / Level"
                  >
                    {userLevel < 2 && <MenuItem value={2}>{LEVEL_NAMES[2]} (Level 2)</MenuItem>}
                    {userLevel < 3 && <MenuItem value={3}>{LEVEL_NAMES[3]} (Level 3)</MenuItem>}
                    {userLevel < 4 && <MenuItem value={4}>{LEVEL_NAMES[4]} (Level 4)</MenuItem>}
                    {userLevel < 5 && <MenuItem value={5}>{LEVEL_NAMES[5]} (Level 5)</MenuItem>}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Pincode"
                  fullWidth
                  value={editForm.pinCode}
                  onChange={(e) => setEditForm({ ...editForm, pinCode: e.target.value })}
                  error={!!editValidationErrors.pinCode}
                  helperText={editValidationErrors.pinCode}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Address"
                  fullWidth
                  multiline
                  rows={2}
                  value={editForm.address}
                  onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setEditModalOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button onClick={handleSaveEditUser} variant="contained" color="primary" sx={{ fontWeight: 700 }}>
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Modal for Deactivate / Activate */}
        <Dialog
          open={confirmStatusModalOpen}
          onClose={() => setConfirmStatusModalOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700, color: 'primary.main' }}>
            Confirm Status Change
          </DialogTitle>
          <DialogContent dividers>
            <DialogContentText sx={{ color: '#334155', fontWeight: 500 }}>
              {targetUserForStatus && (
                <>
                  Are you sure you want to{' '}
                  <strong>
                    {(targetUserForStatus.status || 'active') === 'active' ? 'deactivate' : 'activate'}
                  </strong>{' '}
                  user <strong>{targetUserForStatus.firstName} {targetUserForStatus.lastName}</strong>?
                </>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setConfirmStatusModalOpen(false)} color="inherit" variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={handleConfirmStatusToggle}
              color={
                targetUserForStatus && (targetUserForStatus.status || 'active') === 'active'
                  ? 'error'
                  : 'success'
              }
              variant="contained"
              sx={{ fontWeight: 700 }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>

        <Dialog open={confirmDialogOpen} onClose={handleConfirmDialogClose}>
          <DialogTitle sx={{ fontWeight: 700 }}>Unsaved Changes</DialogTitle>
          <DialogContent>
            <Typography>You have unsaved changes. Do you really want to close?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleConfirmDialogClose} color="secondary">
              Cancel
            </Button>
            <Button onClick={handleConfirmDialogProceed} color="primary" variant="contained">
              Proceed
            </Button>
          </DialogActions>
        </Dialog>
    </Box>
  );
};

export default CreateAdmin;
